export type AuthUser={sub:string;email:string;name:string}
type StoredTokens={accessToken:string;idToken:string;refreshToken?:string;expiresAt:number}

const domain=(import.meta.env.VITE_COGNITO_DOMAIN as string|undefined)?.replace(/\/$/,'')??''
const clientId=(import.meta.env.VITE_COGNITO_CLIENT_ID as string|undefined)??''
const redirectUri=`${window.location.origin}/auth/callback`
const tokenKey='lucky-estates-auth-v1'
const verifierKey='lucky-estates-pkce-verifier'
const stateKey='lucky-estates-oauth-state'
const errorKey='lucky-estates-auth-error'

const encode=(bytes:Uint8Array)=>btoa(String.fromCharCode(...bytes)).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'')
const random=(length=48)=>{const bytes=new Uint8Array(length);crypto.getRandomValues(bytes);return encode(bytes)}
const challenge=async(value:string)=>encode(new Uint8Array(await crypto.subtle.digest('SHA-256',new TextEncoder().encode(value))))
const readTokens=():StoredTokens|null=>{try{return JSON.parse(localStorage.getItem(tokenKey)??'null') as StoredTokens|null}catch{return null}}
const saveTokens=(payload:{access_token:string;id_token:string;refresh_token?:string;expires_in:number},previous?:StoredTokens|null)=>{
  const next:StoredTokens={accessToken:payload.access_token,idToken:payload.id_token,refreshToken:payload.refresh_token??previous?.refreshToken,expiresAt:Date.now()+payload.expires_in*1000}
  localStorage.setItem(tokenKey,JSON.stringify(next));localStorage.removeItem(errorKey);return next
}
const decode=(token:string)=>{try{const part=token.split('.')[1].replace(/-/g,'+').replace(/_/g,'/');return JSON.parse(decodeURIComponent(Array.from(atob(part),c=>`%${c.charCodeAt(0).toString(16).padStart(2,'0')}`).join(''))) as Record<string,unknown>}catch{return {}}}

export const authConfigured=Boolean(domain&&clientId)
export const googleConfigured=(import.meta.env.VITE_GOOGLE_LOGIN_ENABLED as string|undefined)==='true'
export const appleConfigured=(import.meta.env.VITE_APPLE_LOGIN_ENABLED as string|undefined)==='true'
export const authError=()=>localStorage.getItem(errorKey)??''

export async function startSignIn(provider?:'Google'|'SignInWithApple'){
  if(!authConfigured)throw new Error('Account sign-in is not configured.')
  const verifier=random(64),state=random(32)
  sessionStorage.setItem(verifierKey,verifier);sessionStorage.setItem(stateKey,state)
  const query=new URLSearchParams({client_id:clientId,response_type:'code',scope:'openid email profile',redirect_uri:redirectUri,code_challenge:await challenge(verifier),code_challenge_method:'S256',state})
  if(provider)query.set('identity_provider',provider)
  window.location.assign(`${domain}/oauth2/authorize?${query}`)
}

export async function completeAuthRedirect(){
  if(window.location.pathname!=='/auth/callback')return
  const query=new URLSearchParams(window.location.search),error=query.get('error')
  if(error){localStorage.setItem(errorKey,query.get('error_description')||'Sign-in was cancelled.');history.replaceState({},'', '/');return}
  const code=query.get('code'),state=query.get('state'),expected=sessionStorage.getItem(stateKey),verifier=sessionStorage.getItem(verifierKey)
  if(!code||!state||!expected||state!==expected||!verifier){localStorage.setItem(errorKey,'The sign-in response could not be verified. Please try again.');history.replaceState({},'', '/');return}
  try{
    const body=new URLSearchParams({grant_type:'authorization_code',client_id:clientId,code,redirect_uri:redirectUri,code_verifier:verifier})
    const response=await fetch(`${domain}/oauth2/token`,{method:'POST',headers:{'Content-Type':'application/x-www-form-urlencoded'},body})
    if(!response.ok)throw new Error('The secure sign-in could not be completed.')
    saveTokens(await response.json())
  }catch(error){localStorage.setItem(errorKey,error instanceof Error?error.message:'The secure sign-in could not be completed.')}finally{
    sessionStorage.removeItem(stateKey);sessionStorage.removeItem(verifierKey);history.replaceState({},'', '/')
  }
}

export async function getAccessToken():Promise<string|null>{
  const current=readTokens();if(!current)return null
  if(current.expiresAt>Date.now()+90_000)return current.accessToken
  if(!current.refreshToken){localStorage.removeItem(tokenKey);return null}
  try{
    const body=new URLSearchParams({grant_type:'refresh_token',client_id:clientId,refresh_token:current.refreshToken})
    const response=await fetch(`${domain}/oauth2/token`,{method:'POST',headers:{'Content-Type':'application/x-www-form-urlencoded'},body})
    if(!response.ok)throw new Error('Session expired')
    return saveTokens(await response.json(),current).accessToken
  }catch{localStorage.removeItem(tokenKey);return null}
}

export function currentUser():AuthUser|null{
  const tokens=readTokens();if(!tokens)return null
  const claims=decode(tokens.idToken),sub=String(claims.sub??''),email=String(claims.email??'')
  if(!sub)return null
  return {sub,email,name:String(claims.name??claims['cognito:username']??email.split('@')[0]??'City Explorer')}
}

export function signOut(){
  localStorage.removeItem(tokenKey);localStorage.removeItem(errorKey)
  if(!authConfigured){window.location.reload();return}
  const query=new URLSearchParams({client_id:clientId,logout_uri:`${window.location.origin}/`})
  window.location.assign(`${domain}/logout?${query}`)
}
